import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import { kycApi } from '@/api/kyc';
import { Loading, ProfileStatusView } from '@/components/common';
import { Flex } from 'antd';
import { usePopups } from '@/hooks';
import { STATUS } from '@/utils/static/kyc';
import { useParams } from 'react-router';

export const ProfileStatus = ({ referralView = false }) => {
  const { showErrorToast } = usePopups();
  const { id: referralId } = useParams();
  const { referralToken } = useSelector((state) => state.user);
  const [isQuestionnairesGenerated, setIsQuestionnairesGenerated] = useState(false);
  const investor = useSelector((state) => state.investor);
  const [rejectedQuestionnaires, setRejectedQuestionnaires] = useState(null);

  const { mutate } = useMutation({ mutationFn: kycApi.generateQuestionnaires });
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['getQuestionnaires', referralToken],
    queryFn: referralView
      ? () => kycApi.getReferralQuestionnaires(referralToken)
      : kycApi.getQuestionnaires,
    enabled: (referralView && !!referralToken) || !referralView,
    refetchInterval: 10000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    onSuccess: async (res) => {
      try {
        const questLength = res.data?.length || 0;
        if (questLength !== 0) {
          const hasUnaccepted = res.data.some((item) => {
            return item.status === STATUS.REJECTED;
          });

          if (hasUnaccepted) {
            const rejRes = await kycApi.getRejectedEntities();
            const sectionTypes = rejRes.data.sectionTypes;
            if (sectionTypes.length > 0) {
              const rejectedQuests = {};
              sectionTypes.forEach((item) => {
                rejectedQuests[item.id] = item.comment[item.comment.length - 1]?.comment;
              });

              setRejectedQuestionnaires(rejectedQuests);
            }
          }
        }
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    }
  });

  const { data: sumSubStatusRes, refetch: refetchSumSubStatus } = useQuery({
    queryKey: ['getSumSubStatus'],
    queryFn: kycApi.getSumSubStatus,
    onSuccess: async (res) => {
      try {
        if (res.data?.myStatus === 'New' || res.data?.myStatus === 'In process') {
          const timeout = new Promise((resolve) => {
            setTimeout(() => resolve('refetch'), 8000);
          });
          await timeout.then(() => {
            refetchSumSubStatus();
          });
        } else if (res.data?.myStatus === 'Rejected') {
          const timeout = new Promise((resolve) => {
            setTimeout(() => resolve('refetch'), 20000);
          });
          await timeout.then(() => {
            refetchSumSubStatus();
          });
        }
      } catch (e) {
        console.error(e);
        showErrorToast(e, true);
      }
    }
  });

  useEffect(() => {
    if (data && !isQuestionnairesGenerated) {
      const questLength = data.data.length || 0;
      if (questLength === 0) {
        (async () => {
          await mutate();
          await refetch();
          setIsQuestionnairesGenerated(true);
        })();
      }
    }
  }, [data, isQuestionnairesGenerated]);

  return (
    <div>
      {!isLoading ? (
        <ProfileStatusView
          data={data}
          investor={investor}
          rejectedQuestionnaires={rejectedQuestionnaires}
          sumSubStatusRes={sumSubStatusRes}
          refetchQuests={refetch}
          referralView={referralView}
          referralId={referralId}
        />
      ) : (
        <Flex vertical justify='center' align='center' style={{ height: 400 }}>
          <Loading />
        </Flex>
      )}
    </div>
  );
};
